import { useCallback, useEffect, useState } from 'react'
import api from '@/services/api'
import type { SecurityEvent, SecurityRiskState, SecuritySummary } from '@/types/security'

const EVENTS_LIMIT = 50
const RISK_LIMIT = 50

export function useBehavioralSecurityPanel() {
  const [summary, setSummary] = useState<SecuritySummary | null>(null)
  const [events, setEvents] = useState<SecurityEvent[]>([])
  const [riskStates, setRiskStates] = useState<SecurityRiskState[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [unblockingKey, setUnblockingKey] = useState<string | null>(null)

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    else setRefreshing(true)
    setError('')
    try {
      const [summaryRes, eventsRes, riskRes] = await Promise.all([
        api.get('/security/summary'),
        api.get('/security/events', { params: { limit: EVENTS_LIMIT } }),
        api.get('/security/risk-states', { params: { limit: RISK_LIMIT } }),
      ])
      setSummary(summaryRes.data.data ?? summaryRes.data)
      setEvents(eventsRes.data.data ?? eventsRes.data ?? [])
      setRiskStates(riskRes.data.data ?? riskRes.data ?? [])
    } catch {
      setError('Falha ao carregar dados de segurança comportamental.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
    const interval = setInterval(() => fetchData(true), 30000)
    return () => clearInterval(interval)
  }, [fetchData])

  const handleUnblock = async (scopeType: string, scopeKey: string) => {
    const key = `${scopeType}:${scopeKey}`
    setUnblockingKey(key)
    try {
      await api.post(
        `/security/risk-states/${encodeURIComponent(scopeType)}/${encodeURIComponent(scopeKey)}/unblock`,
      )
      await fetchData(true)
    } catch {
      setError('Não foi possível desbloquear este escopo.')
    } finally {
      setUnblockingKey(null)
    }
  }

  return {
    summary,
    events,
    riskStates,
    loading,
    refreshing,
    error,
    unblockingKey,
    fetchData,
    handleUnblock,
    eventsLimit: EVENTS_LIMIT,
  }
}
